import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Tag, Row, Col, DatePicker, Alert, Progress, Tooltip, Upload, Space } from 'antd';
import { PlusOutlined, SafetyCertificateOutlined, UploadOutlined, FileOutlined, DeleteOutlined, EyeOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';
import ApiService from '../services/api';
import dayjs from 'dayjs';

const TRUCK_DOCUMENT_TYPES = [
  { key: 'registration_book', label: 'Vehicle Registration Book', required: true },
  { key: 'road_license', label: 'Road License (Vehicle Disc)', required: true },
  { key: 'fitness_certificate', label: 'Certificate of Fitness (COF)', required: true },
  { key: 'insurance', label: 'Insurance Certificate', required: true },
  { key: 'other', label: 'Other Documents', required: false }
];

const Trucks = () => {
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [complianceModal, setComplianceModal] = useState(false);
  const [filesModal, setFilesModal] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [complianceForm] = Form.useForm();

  useEffect(() => {
    loadTrucks();
  }, []);

  const loadTrucks = async () => {
    try {
      const data = await ApiService.getDashboardData();
      setTrucks(data.trucks || []);
      setDrivers(data.drivers || []);
    } catch (error) {
      console.error('Error loading trucks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTruck = async (values) => {
    try {
      const truckCount = trucks.length;
      const truckId = `TRK${String(truckCount + 1).padStart(3, '0')}`;
      
      const truckData = {
        ...values,
        truck_id: truckId,
        road_license_expiry: values.road_license_expiry?.format('YYYY-MM-DD'),
        fitness_expiry: values.fitness_expiry?.format('YYYY-MM-DD'),
        insurance_expiry: values.insurance_expiry?.format('YYYY-MM-DD')
      };
      
      await ApiService.addTruck(truckData);
      message.success('Truck added successfully');
      setModalVisible(false);
      form.resetFields();
      loadTrucks();
    } catch (error) {
      message.error('Error adding truck');
    }
  };

  const handleUpdateCompliance = async (values) => {
    try {
      const complianceData = {
        road_license_expiry: values.road_license_expiry?.format('YYYY-MM-DD'),
        fitness_expiry: values.fitness_expiry?.format('YYYY-MM-DD'),
        insurance_expiry: values.insurance_expiry?.format('YYYY-MM-DD'),
        registration_number: values.registration_number,
        engine_number: values.engine_number,
        chassis_number: values.chassis_number
      };
      
      await ApiService.updateTruck(selectedTruck.truck_id, complianceData);
      message.success('Compliance updated');
      setComplianceModal(false);
      loadTrucks();
    } catch (error) {
      message.error(`Error: ${error.message}`);
    }
  };

  const handleEditTruck = async (values) => {
    try {
      const updateData = {
        make: values.make,
        model: values.model,
        year: values.year,
        license_plate: values.license_plate,
        current_odometer: values.current_odometer
      };
      
      await ApiService.updateTruck(selectedTruck.truck_id, updateData);
      message.success('Truck info updated');
      setEditModal(false);
      loadTrucks();
    } catch (error) {
      message.error(`Error: ${error.message}`);
    }
  };

  const openEditModal = (truck) => {
    setSelectedTruck(truck);
    editForm.setFieldsValue({
      make: truck.make,
      model: truck.model,
      year: truck.year,
      license_plate: truck.license_plate,
      current_odometer: truck.current_odometer
    });
    setEditModal(true);
  };

  const openComplianceModal = (truck) => {
    setSelectedTruck(truck);
    complianceForm.setFieldsValue({
      road_license_expiry: truck.road_license_expiry ? dayjs(truck.road_license_expiry) : null,
      fitness_expiry: truck.fitness_expiry ? dayjs(truck.fitness_expiry) : null,
      insurance_expiry: truck.insurance_expiry ? dayjs(truck.insurance_expiry) : null,
      registration_number: truck.registration_number,
      engine_number: truck.engine_number,
      chassis_number: truck.chassis_number
    });
    setComplianceModal(true);
  };

  const openFilesModal = (truck) => {
    setSelectedTruck(truck);
    setFilesModal(true);
  };

  const handleFileUpload = async (file, documentType) => {
    if (!selectedTruck) return false;
    
    setUploading(true);
    try {
      const response = await ApiService.generateTruckUploadUrl(
        selectedTruck.truck_id,
        file.name,
        file.type,
        documentType
      );
      
      await fetch(response.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });
      
      const currentDocs = selectedTruck.documents || [];
      const newDoc = {
        type: documentType,
        fileName: file.name,
        fileUrl: response.fileUrl,
        fileKey: response.fileKey,
        uploadedAt: new Date().toISOString()
      };
      
      const updatedDocs = [...currentDocs, newDoc];
      await ApiService.updateTruckDocuments(selectedTruck.truck_id, updatedDocs);
      
      setSelectedTruck({ ...selectedTruck, documents: updatedDocs });
      message.success(`${file.name} uploaded`);
      loadTrucks();
    } catch (error) {
      message.error(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDeleteDocument = async (docIndex) => {
    if (!selectedTruck) return;
    
    try {
      const updatedDocs = selectedTruck.documents.filter((_, idx) => idx !== docIndex);
      await ApiService.updateTruckDocuments(selectedTruck.truck_id, updatedDocs);
      setSelectedTruck({ ...selectedTruck, documents: updatedDocs });
      message.success('Document removed');
      loadTrucks();
    } catch (error) {
      message.error(`Error: ${error.message}`);
    }
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'missing', color: 'default', text: 'Not Set' };
    const today = dayjs();
    const expiry = dayjs(expiryDate);
    const daysUntil = expiry.diff(today, 'day');
    
    if (daysUntil < 0) return { status: 'expired', color: 'red', text: 'Expired' };
    if (daysUntil <= 30) return { status: 'critical', color: 'red', text: `${daysUntil}d left` };
    if (daysUntil <= 60) return { status: 'warning', color: 'orange', text: `${daysUntil}d left` };
    if (daysUntil <= 90) return { status: 'soon', color: 'gold', text: `${daysUntil}d left` };
    return { status: 'valid', color: 'green', text: 'Valid' };
  };

  const getComplianceScore = (truck) => {
    let score = 0;
    const total = 3;
    
    if (truck.road_license_expiry && getExpiryStatus(truck.road_license_expiry).status === 'valid') score++;
    if (truck.fitness_expiry && getExpiryStatus(truck.fitness_expiry).status === 'valid') score++;
    if (truck.insurance_expiry && getExpiryStatus(truck.insurance_expiry).status === 'valid') score++;
    
    return Math.round((score / total) * 100);
  };

  const getExpiringDocuments = () => {
    const alerts = [];
    trucks.forEach(truck => {
      const docs = [
        { name: 'Road License', expiry: truck.road_license_expiry },
        { name: 'Certificate of Fitness', expiry: truck.fitness_expiry },
        { name: 'Insurance', expiry: truck.insurance_expiry }
      ];
      docs.forEach(doc => {
        const status = getExpiryStatus(doc.expiry);
        if (status.status === 'expired' || status.status === 'critical' || status.status === 'warning') {
          alerts.push({ truck: truck.license_plate, document: doc.name, status, expiry: doc.expiry });
        }
      });
    });
    return alerts.sort((a, b) => dayjs(a.expiry).diff(dayjs(b.expiry)));
  };

  const expiringDocs = getExpiringDocuments();

  const columns = [
    { 
      title: 'Vehicle', 
      key: 'vehicle',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.license_plate}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.make} {record.model} ({record.year})</div>
        </div>
      )
    },
    {
      title: 'Compliance',
      key: 'compliance',
      render: (_, record) => {
        const score = getComplianceScore(record);
        return (
          <Tooltip title={`${score}% compliant`}>
            <Progress 
              percent={score} 
              size="small" 
              status={score < 50 ? 'exception' : score < 80 ? 'normal' : 'success'}
              style={{ width: 80 }}
            />
          </Tooltip>
        );
      }
    },
    {
      title: 'Documents',
      key: 'documents',
      render: (_, record) => {
        const docCount = record.documents?.length || 0;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tooltip title={`Road License: ${getExpiryStatus(record.road_license_expiry).text}`}>
              <Tag color={getExpiryStatus(record.road_license_expiry).color}>RL</Tag>
            </Tooltip>
            <Tooltip title={`COF: ${getExpiryStatus(record.fitness_expiry).text}`}>
              <Tag color={getExpiryStatus(record.fitness_expiry).color}>COF</Tag>
            </Tooltip>
            <Tooltip title={`Insurance: ${getExpiryStatus(record.insurance_expiry).text}`}>
              <Tag color={getExpiryStatus(record.insurance_expiry).color}>INS</Tag>
            </Tooltip>
            {docCount > 0 && <Tag color="blue">{docCount} files</Tag>}
          </div>
        );
      }
    },
    { 
      title: 'Driver', 
      key: 'driver_name',
      render: (record) => {
        const driver = drivers.find(d => d.driver_id === record.driver_id);
        return driver ? (
          <Tag color="blue">{driver.name}</Tag>
        ) : (
          <span style={{ color: 'orange' }}>Unassigned</span>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>Edit</Button>
          <Button size="small" icon={<SafetyCertificateOutlined />} onClick={() => openComplianceModal(record)}>Compliance</Button>
          <Button size="small" icon={<FileOutlined />} onClick={() => openFilesModal(record)}>Files</Button>
        </Space>
      )
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>Fleet Management</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>Add Truck</Button>
      </div>

      {expiringDocs.length > 0 && (
        <Alert
          message={`${expiringDocs.length} Vehicle Document Alert${expiringDocs.length > 1 ? 's' : ''}`}
          description={
            <div style={{ maxHeight: 100, overflow: 'auto' }}>
              {expiringDocs.slice(0, 5).map((alert, idx) => (
                <div key={idx} style={{ fontSize: 12 }}>
                  <WarningOutlined style={{ color: alert.status.color, marginRight: 4 }} />
                  <strong>{alert.truck}</strong>: {alert.document} - {alert.status.text}
                </div>
              ))}
              {expiringDocs.length > 5 && <div style={{ fontSize: 12, color: '#666' }}>...and {expiringDocs.length - 5} more</div>}
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <Table dataSource={trucks} columns={columns} loading={loading} rowKey="truck_id" size="middle" />
      </Card>

      {/* Add Truck Modal */}
      <Modal title="Add New Truck" open={modalVisible} onCancel={() => setModalVisible(false)} onOk={() => form.submit()} width={600}>
        <Form form={form} onFinish={handleAddTruck} layout="vertical">
          <Alert message="Zimbabwe VID Requirements" description="Road License, Certificate of Fitness (COF), and Insurance are mandatory for commercial vehicles." type="info" showIcon style={{ marginBottom: 16 }} />
          <Row gutter={16}>
            <Col span={12}><Form.Item name="make" label="Make" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="model" label="Model" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="year" label="Year" rules={[{ required: true }]}><Input type="number" /></Form.Item></Col>
            <Col span={12}><Form.Item name="license_plate" label="License Plate" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="road_license_expiry" label="Road License Expiry"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="fitness_expiry" label="COF Expiry"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="insurance_expiry" label="Insurance Expiry"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* Edit Truck Modal */}
      <Modal title={<span><EditOutlined /> Edit Truck - {selectedTruck?.license_plate}</span>} open={editModal} onCancel={() => setEditModal(false)} onOk={() => editForm.submit()} width={500}>
        <Form form={editForm} onFinish={handleEditTruck} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="make" label="Make" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="model" label="Model" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="year" label="Year" rules={[{ required: true }]}><Input type="number" /></Form.Item></Col>
            <Col span={12}><Form.Item name="license_plate" label="License Plate" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={24}><Form.Item name="current_odometer" label="Current Odometer (km)"><Input type="number" /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* Compliance Modal */}
      <Modal title={<span><SafetyCertificateOutlined /> {selectedTruck?.license_plate} - Compliance</span>} open={complianceModal} onCancel={() => setComplianceModal(false)} onOk={() => complianceForm.submit()} width={600}>
        <Form form={complianceForm} onFinish={handleUpdateCompliance} layout="vertical">
          <Alert message="Zimbabwe VID Requirements" description="All commercial vehicles must have valid Road License, COF, and Insurance." type="info" showIcon style={{ marginBottom: 16 }} />
          <Row gutter={16}>
            <Col span={12}><Form.Item name="road_license_expiry" label="Road License Expiry"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="fitness_expiry" label="Certificate of Fitness Expiry"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="insurance_expiry" label="Insurance Expiry"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="registration_number" label="Registration Number"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="engine_number" label="Engine Number"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="chassis_number" label="Chassis Number"><Input /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* Files Upload Modal */}
      <Modal title={<span><FileOutlined /> {selectedTruck?.license_plate} - Document Files</span>} open={filesModal} onCancel={() => setFilesModal(false)} footer={<Button onClick={() => setFilesModal(false)}>Close</Button>} width={600}>
        <Alert message="Upload vehicle documents (PDF or images)" type="info" showIcon style={{ marginBottom: 16 }} />
        
        {TRUCK_DOCUMENT_TYPES.map(docType => (
          <div key={docType.key} style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 500 }}>
                {docType.label} {docType.required && <span style={{ color: 'red' }}>*</span>}
              </span>
              <Upload accept=".pdf,.jpg,.jpeg,.png" showUploadList={false} beforeUpload={(file) => handleFileUpload(file, docType.key)} disabled={uploading}>
                <Button size="small" icon={<UploadOutlined />} loading={uploading}>Upload</Button>
              </Upload>
            </div>
            
            {selectedTruck?.documents?.filter(d => d.type === docType.key).map((doc, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', background: '#fff', borderRadius: 4, marginTop: 4 }}>
                <span style={{ fontSize: 12 }}>{doc.fileName}</span>
                <Space size="small">
                  <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => window.open(doc.fileUrl, '_blank')} />
                  <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteDocument(selectedTruck.documents.indexOf(doc))} />
                </Space>
              </div>
            ))}
          </div>
        ))}
      </Modal>
    </div>
  );
};

export default Trucks;
